import React from "react";
import { Drawer, Upload, Button, message, Modal, Typography, Spin, Divider, Space } from "antd";
import { InboxOutlined, DeleteOutlined, UploadOutlined } from "@ant-design/icons";
import {
  getCampaignConfig,
  updateCampaignConfig,
  deleteCampaignDefaultLogo,
  uploadCampaignImage,
} from "services/reward-campaign-service";

const { Dragger } = Upload;
const { Title, Paragraph } = Typography;

const ACCEPTED_TYPES = ["image/png", "image/jpeg", "image/jpg"];
const MAX_SIZE_MB = 1;

const RewardCampaignConfigDrawer = ({ visible, onClose }) => {
  const [defaultLogoUrl, setDefaultLogoUrl] = React.useState(null);
  const [previewUrl, setPreviewUrl] = React.useState(null);
  const [pendingFile, setPendingFile] = React.useState(null);
  const [loadingConfig, setLoadingConfig] = React.useState(false);
  const [uploading, setUploading] = React.useState(false);
  const [deleting, setDeleting] = React.useState(false);

  React.useEffect(() => {
    if (visible) {
      loadConfig();
    } else {
      clearPending();
    }
  }, [visible]);

  const clearPending = () => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setPreviewUrl(null);
    setPendingFile(null);
  };

  const loadConfig = async () => {
    setLoadingConfig(true);
    try {
      const data = await getCampaignConfig();
      setDefaultLogoUrl(data.defaultLogoUrl || null);
    } catch (e) {
      message.error("Erro ao carregar configuração.");
    } finally {
      setLoadingConfig(false);
    }
  };

  const beforeUpload = (file) => {
    if (!ACCEPTED_TYPES.includes(file.type)) {
      message.error("Formato inválido. Use PNG, JPG ou JPEG.");
      return false;
    }
    if (file.size / 1024 / 1024 > MAX_SIZE_MB) {
      message.error(`Imagem muito grande. Tamanho máximo: ${MAX_SIZE_MB}MB.`);
      return false;
    }
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setPreviewUrl(URL.createObjectURL(file));
    setPendingFile(file);
    return false;
  };

  const handleSave = async () => {
    if (!pendingFile) return;
    setUploading(true);
    try {
      const uploadData = await uploadCampaignImage(pendingFile);
      await updateCampaignConfig(uploadData.url);
      clearPending();
      setDefaultLogoUrl(uploadData.url);
      message.success("Logo padrão atualizada com sucesso.");
    } catch (e) {
      message.error("Erro ao fazer upload da imagem.");
    } finally {
      setUploading(false);
    }
  };

  const handleCancel = () => {
    clearPending();
    onClose();
  };

  const handleDelete = () => {
    Modal.confirm({
      title: "Excluir logo padrão",
      content: "Tem certeza que deseja excluir a logo padrão? Esta ação não pode ser desfeita.",
      okText: "Excluir",
      okType: "danger",
      cancelText: "Cancelar",
      onOk: async () => {
        setDeleting(true);
        try {
          await deleteCampaignDefaultLogo();
          setDefaultLogoUrl(null);
          message.success("Logo padrão removida.");
        } catch (e) {
          message.error("Erro ao remover logo padrão.");
        } finally {
          setDeleting(false);
        }
      },
    });
  };

  const displayUrl = previewUrl || defaultLogoUrl;

  return (
    <Drawer
      title="Configuração"
      open={visible}
      onClose={handleCancel}
      width={480}
      placement="right"
      destroyOnClose
      footer={
        pendingFile ? (
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <Space>
              <Button onClick={handleCancel} disabled={uploading}>Cancelar</Button>
              <Button type="primary" loading={uploading} onClick={handleSave}>Salvar</Button>
            </Space>
          </div>
        ) : null
      }
    >
      <Spin spinning={loadingConfig}>
        <Title level={5} style={{ marginTop: 0 }}>Imagem</Title>
        <Paragraph type="secondary" style={{ fontSize: 13 }}>
          Esta imagem será utilizada como padrão em suas campanhas quando não houver uma logo específica configurada.
        </Paragraph>
        <ul style={{ paddingLeft: 16, marginBottom: 16, fontSize: 13, color: "rgba(0,0,0,0.45)" }}>
          <li>Tamanho limite: 1MB</li>
          <li>Formatos aceitos: PNG, JPEG e JPG</li>
          <li>Dimensões sugeridas: 200×200 até 400×400px</li>
          <li>Toda comunicação do Link+ segue o fundo branco, logo solicitamos que seu logo seja escuro ou que contraste com o branco</li>
        </ul>

        <Dragger
          accept=".png,.jpg,.jpeg"
          maxCount={1}
          showUploadList={false}
          beforeUpload={beforeUpload}
          disabled={uploading}
        >
          <UploadOutlined style={{ fontSize: '1.5rem' }} />
          <p>Clique aqui ou arraste sua imagem para fazer o upload</p>
        </Dragger>

        {displayUrl && (
          <>
            <Divider />
            <Title level={5}>{previewUrl ? "Prévia" : "Imagem Padrão Atual"}</Title>
            <div style={{ display: "flex", alignItems: "flex-start", gap: 16 }}>
              <img
                src={displayUrl}
                alt="Logo"
                style={{
                  width: 80,
                  height: 80,
                  objectFit: "contain",
                  border: "1px solid #f0f0f0",
                  borderRadius: 4,
                }}
              />
              {!previewUrl && (
                <Button
                  icon={<DeleteOutlined />}
                  danger
                  loading={deleting}
                  onClick={handleDelete}
                >
                  Excluir
                </Button>
              )}
            </div>
          </>
        )}
      </Spin>
    </Drawer>
  );
};

export default RewardCampaignConfigDrawer;
